/**
 * <TraceableValue /> — clickable wrapper around a computed value.
 *
 * Per DESIGN_SYSTEM.md §6 "The trace-on-click pattern":
 *   - Every number, value, or decision computed by the system is
 *     clickable to reveal its trace. This is not optional — it's how
 *     trust gets built.
 *   - Numbers have a subtle dotted underline on hover.
 *   - Click → opens a drawer (NOT a modal) with the
 *     OverrideStackViewer showing every layer that contributed.
 *   - Includes citation back to source rule / customer policy / AI
 *     extraction.
 *
 * Wrap any displayed entitlement, balance, eligibility result, or
 * computed amount with this primitive. The drawer is provided by the
 * caller via `onClick` — the primitive doesn't own routing.
 *
 * Usage:
 *   <TraceableValue onTraceClick={() => openDrawer(balanceId)}>
 *     {balance} days
 *   </TraceableValue>
 */

import type { ReactNode } from "react";

export interface TraceableValueProps {
  /** Children — typically the displayed value (number + unit). */
  children: ReactNode;
  /** Fired when the user clicks. Caller opens the trace drawer. */
  onTraceClick: () => void;
  /** Accessible label. Default "View calculation trace". */
  "aria-label"?: string;
  /** Optional className passthrough. */
  className?: string;
  /** When false, suppresses the dotted underline. Use sparingly —
   * the dotted underline IS the affordance that says "click for
   * trace". */
  showUnderline?: boolean;
}

export function TraceableValue({
  children,
  onTraceClick,
  "aria-label": ariaLabel = "View calculation trace",
  className = "",
  showUnderline = true,
}: TraceableValueProps): ReactNode {
  return (
    <button
      type="button"
      onClick={onTraceClick}
      aria-label={ariaLabel}
      className={[
        "inline-flex items-baseline",
        "cursor-help",
        // Dotted underline is the affordance. Hover thickens it.
        showUnderline
          ? "decoration-dotted underline-offset-2 hover:underline"
          : "",
        // Inherit the surrounding text color; this primitive
        // visually disappears except for the underline.
        "text-inherit",
        "transition-colors duration-micro ease-out",
        "focus-visible:outline-none focus-visible:underline",
        "focus-visible:ring-0",
        // Tabular-nums keeps the digit widths consistent so values
        // don't jitter when the trace drawer opens/closes.
        "tabular-nums",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}
